import { apiFetch } from '../../services/http';

export type Category = {
  id: string;
  name: string;
  slug: string;
  description: string;
  displayOrder: number;
  createdAt: string;
};

export type TopicStatistics = {
  totalThreads: number;
  totalSubthreads: number;
  totalPosts: number;
  totalComments: number;
  lastThreadTitle?: string;
  lastThreadId?: string;
  lastPostTime?: string;
  lastAuthorAlias?: string;
};

export type Topic = {
  id: string;
  categoryId?: string;
  slug: string;
  displayOrder: number;
  title: string;
  description: string;
  createdAt: string;
  stats?: TopicStatistics;
};

export type Thread = {
  id: string;
  topicId: string;
  topicSlug?: string;
  title: string;
  authorAlias: string;
  createdAt: string;
  pinned: boolean;
  locked: boolean;
  isSubthread: boolean;
  parentSubthreadId?: string;
  viewCount: number;
  replyCount: number;
  lastCommentAlias?: string;
  lastCommentTime?: string;
};

export type Post = {
  id: string;
  threadId: string;
  parentPostId?: string;
  authorAlias: string;
  body: string;
  attachments?: string[];
  createdAt: string;
};

type ThreadPayload = {
  title: string;
  body: string;
  attachments?: string[];
  attachmentsFiles?: File[];
  isSubthread?: boolean;
  parentSubthreadId?: string;
};

type PostPayload = {
  body: string;
  attachments?: string[];
  attachmentsFiles?: File[];
  parentPostId?: string;
};

export type ThreadWithPosts = {
  thread: Thread;
  posts: Post[];
  participantAliases?: string[];
};

export type SubthreadWithThreads = {
  subthread: Thread;
  threads: Thread[];
};

export type TopicThreadsResponse = {
  subthreads: SubthreadWithThreads[];
  regularThreads: Thread[];
};

export async function fetchTopics(limit = 20, withStats = false): Promise<Topic[]> {
  const statsParam = withStats ? '&withStats=true' : '';
  return apiFetch<Topic[]>(`/api/forum/topics?limit=${limit}${statsParam}`);
}

export async function fetchTopic(topicId: string): Promise<Topic> {
  const topics = await fetchTopics(100);
  const topic = topics.find(t => t.id === topicId);
  if (!topic) {
    throw new Error('Topic not found');
  }
  return topic;
}

export async function fetchThreads(topicId: string, limit = 50): Promise<Thread[]> {
  return apiFetch<Thread[]>(`/api/forum/topics/${topicId}/threads?limit=${limit}`);
}

export async function fetchThreadsOrganized(topicId: string, limit = 50): Promise<TopicThreadsResponse> {
  return apiFetch<TopicThreadsResponse>(`/api/forum/topics/${topicId}/threads?organized=true&limit=${limit}`);
}

export async function fetchThread(threadId: string): Promise<ThreadWithPosts> {
  return apiFetch<ThreadWithPosts>(`/api/forum/threads/${threadId}/`);
}

export async function fetchThreadPaginated(threadId: string, limit: number, offset: number): Promise<ThreadWithPosts> {
  return apiFetch<ThreadWithPosts>(`/api/forum/threads/${threadId}/?limit=${limit}&offset=${offset}`);
}

export async function createThread(topicId: string, payload: ThreadPayload): Promise<ThreadWithPosts> {
  const { attachmentsFiles, ...rest } = payload;
  const safeAttachments = (rest.attachments || []).filter((att) => !att.startsWith('data:'));

  if (attachmentsFiles && attachmentsFiles.length > 0) {
    const formData = new FormData();
    formData.append('title', rest.title);
    formData.append('body', rest.body);
    if (rest.isSubthread !== undefined) {
      formData.append('isSubthread', String(rest.isSubthread));
    }
    if (rest.parentSubthreadId) {
      formData.append('parentSubthreadId', rest.parentSubthreadId);
    }
    safeAttachments.forEach((att) => formData.append('attachments', att));
    attachmentsFiles.forEach((file) => formData.append('attachments', file));

    return apiFetch<ThreadWithPosts>(`/api/forum/topics/${topicId}/threads`, {
      method: 'POST',
      body: formData,
    });
  }

  return apiFetch<ThreadWithPosts>(`/api/forum/topics/${topicId}/threads`, {
    method: 'POST',
    body: JSON.stringify({ ...rest, attachments: safeAttachments }),
  });
}

export async function createPost(threadId: string, payload: PostPayload): Promise<Post> {
  const { attachmentsFiles, ...rest } = payload;
  const safeAttachments = (rest.attachments || []).filter((att) => !att.startsWith('data:'));

  if (attachmentsFiles && attachmentsFiles.length > 0) {
    const formData = new FormData();
    formData.append('body', rest.body);
    if (rest.parentPostId) {
      formData.append('parentPostId', rest.parentPostId);
    }
    safeAttachments.forEach((att) => formData.append('attachments', att));
    attachmentsFiles.forEach((file) => formData.append('attachments', file));

    return apiFetch<Post>(`/api/forum/threads/${threadId}/posts`, {
      method: 'POST',
      body: formData,
    });
  }

  return apiFetch<Post>(`/api/forum/threads/${threadId}/posts`, {
    method: 'POST',
    body: JSON.stringify({ ...rest, attachments: safeAttachments }),
  });
}

export async function updateThreadModeration(threadId: string, payload: { pinned?: boolean; locked?: boolean }): Promise<Thread> {
  return apiFetch<Thread>(`/api/forum/threads/${threadId}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function activateAlias(topicId: string, alias: string): Promise<void> {
  const sessionToken = localStorage.getItem('forum.sessionToken');
  if (!sessionToken) {
    throw new Error('Missing session token. Authenticate with WebAuthn first.');
  }

  await apiFetch(`/api/aliases/activate`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({ topicId, alias }),
  });
}

export async function createThreadReport(threadId: string, payload: { reporterAlias: string; reason: string }) {
  return apiFetch(`/api/forum/threads/${threadId}/reports`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createPostReport(postId: string, payload: { reporterAlias: string; reason: string }) {
  return apiFetch(`/api/forum/posts/${postId}/reports`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type Report = {
  id: string;
  targetType: string;
  targetId: string;
  reason: string;
  reporterAlias: string;
  status: string;
  createdAt: string;
  resolvedAt?: string;
  resolutionNote?: string;
};

export async function fetchReports(status?: string, limit = 100): Promise<Report[]> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  return apiFetch<Report[]>(`/api/forum/reports?${params.toString()}`);
}

export async function resolveReport(reportId: string, payload: { status: string; note?: string }) {
  return apiFetch(`/api/forum/reports/${reportId}/resolve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Category API functions

export async function fetchCategories(): Promise<Category[]> {
  return apiFetch<Category[]>('/api/forum/categories');
}

export async function createCategory(payload: { name: string; slug: string; description: string; displayOrder: number }): Promise<Category> {
  return apiFetch<Category>('/api/forum/categories', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCategory(categoryId: string, payload: { name: string; slug: string; description: string; displayOrder: number }): Promise<Category> {
  return apiFetch<Category>(`/api/forum/categories/${categoryId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteCategory(categoryId: string): Promise<void> {
  await apiFetch(`/api/forum/categories/${categoryId}`, {
    method: 'DELETE',
  });
}
